import { api } from 'domains/api';
import { BASE_ANONYMOUS_API_ROUTES, getBaseAPIRoute } from 'domains/api/base/routes';
import { EvaluationDuration } from 'domains/calendars/types';
import { Relationship } from 'domains/client';
import { RelationshipCode } from 'domains/signupDirect/businessLogic/relationships';
import { InsuranceAuthStatus } from 'domains/billing/types';
import { StateAbbrev } from 'utils/states';
import { get, has, isObject, isString } from 'lodash';

export type SignupSchedule = {
  duration: EvaluationDuration;
  timeSlot: string;
  calendarID: string;
};

export type InsuranceAuthorizations = {
  evaluationAuthStatus: InsuranceAuthStatus;
  sessionAuthStatus: InsuranceAuthStatus;
  visitLimits: boolean;
  remainingVisitsLeft: number;
  dateLimits: boolean;
  visitsCompletedBy: string;
  reEvaluationRequired: boolean;
  reEvaluationDueBy: string | null;
};

export type ScheduleSignupEvaluationRequest = {
  address: { state: StateAbbrev };
  amount?: number;
  coPay?: number;
  clientFirstName: string;
  clientLastName: string;
  clientSex?: string;
  /** Present when signing up a client that will belong to an existing (logged in) contact */
  contactID?: string;
  contactFirstName: string;
  contactLastName: string;
  contactTimeZone: string;
  dob: string;
  email: string;
  /** Present when signing up a brand new client */
  password?: string;
  isClientMinor: boolean;
  phone: { mobile: string };
  relationshipToClient: Relationship;
  leadID: string;

  schedules: SignupSchedule[];
  schedulesTimezone: string;

  stripeAccountID?: string;
  entity?: string;
  stripePaymentMethodID?: string;
  skipStripePayment: boolean;
  isMedicaidInsurance?: boolean;
  isMedicareInsurance?: boolean;
  isCommercialInsurance?: boolean;
  isInsuranceOutOfNetwork?: boolean;
  primaryInsurance?: {
    insuredFirstName: string;
    insuredLastName: string;
    insuredDob: Date;
    insuredRelationshipToClient: RelationshipCode;
    insurancePayer: string;
    insurancePayerName: string;
    insuredPayerType: string;
    insuredMemberID: string;
    insuredGroupID?: string;
  };
  insuranceAuthorizations?: Partial<InsuranceAuthorizations>;
  isResumeFlow: boolean;

  prefilledAppointmentID?: number;

  clinicalAreas: string[];
  clinicalConcerns: string[];
  filteringClinicalAreas: string[];
  mappedClinicalConcerns: Record<string, string[]>;
};

export enum SignupErrorCode {
  CARD_ISSUE = 'CARD_ISSUE',
  EMAIL_EXISTS = 'EMAIL_EXISTS',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

export type ScheduleSignupEvaluationResponse =
  | {
      status: 'ok';
      leadID: string;
      calendarID: string;
    }
  | {
      status: 'signup failed';
      error: {
        code: SignupErrorCode;
        message?: string;
      };
    };

export type ScheduleSignupEvaluationResponseError = Extract<
  ScheduleSignupEvaluationResponse,
  { status: 'signup failed' }
>;

export const scheduleEvaluationFromSignupV3 = (
  data: ScheduleSignupEvaluationRequest,
): Promise<ScheduleSignupEvaluationResponse> => {
  return api.baseAnonymous
    .post<ScheduleSignupEvaluationResponse>(
      getBaseAPIRoute(BASE_ANONYMOUS_API_ROUTES.signup['schedule-evaluation']),
      data,
    )
    .catch<ScheduleSignupEvaluationResponse>(e => {
      const response = get(e, 'originalError.response.data');

      if (isObject(response) && has(response, 'error.code')) {
        const errorCode = get(response, 'error.code');
        if (isString(errorCode) && Object.values(SignupErrorCode).includes(errorCode)) {
          return response as unknown as ScheduleSignupEvaluationResponse;
        }
      }

      return {
        status: 'signup failed',
        error: {
          code: SignupErrorCode.UNKNOWN_ERROR,
          message: e.message,
        },
      };
    });
};
